import React from 'react';
import './ActivityCard.css';
import { SkillCategoryNames } from '../services/api';

const ActivityCard = ({
  activity,
  mode = 'view', // 'view', 'assign', 'user'
  hasAssignments = false,
  assignmentCount = 0,
  canDelete = false,
  onAssignClick,
  onViewAssignments,
  onDeleteActivity,
  onGuidanceClick,
  onStatusChange,
  onViewDetails
}) => {
  const getDifficultyLabel = (level) => {
    const labels = {
      1: 'Very Easy',
      2: 'Easy',
      3: 'Medium',
      4: 'Hard',
      5: 'Very Hard'
    };
    return labels[level] || 'Unknown';
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      1: 'status-pending',      // Pending
      2: 'status-in-progress',  // InProgress
      3: 'status-completed',    // Completed
      4: 'status-cancelled',    // Cancelled
      5: 'status-on-hold'       // OnHold
    };
    return classes[status] || 'status-pending';
  };

  const getStatusLabel = (status) => {
    const labels = {
      1: 'Pending',
      2: 'In Progress',
      3: 'Completed',
      4: 'Cancelled',
      5: 'On Hold'
    };
    return labels[status] || 'Unknown';
  };

  return (
    <div className="activity-card">
      <div className="activity-card-header">
        <h3 className="activity-card-title">
          {mode === 'user' && activity.activityTask
            ? activity.activityTask.name
            : activity.name}
        </h3>
        <div className="activity-card-header-actions">
          {mode === 'user' && activity.status && (
            <span className={`status-badge ${getStatusBadgeClass(activity.status)}`}>
              {getStatusLabel(activity.status)}
            </span>
          )}
          {mode === 'assign' && canDelete && onDeleteActivity && (
            <button
              className="btn-delete-icon"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteActivity(activity);
              }}
              title="Delete Activity"
            >
              🗑️
            </button>
          )}
        </div>
      </div>

      <p className="activity-card-description">
        {mode === 'user' && activity.activityTask
          ? activity.activityTask.description
          : activity.description}
      </p>

      {/* Task Metadata */}
      <div className="activity-metadata">
        <div className="metadata-item">
          <span className="metadata-label">Difficulty:</span>
          <span className="metadata-value">
            {getDifficultyLabel(
              mode === 'user' && activity.activityTask
                ? activity.activityTask.genericDifficulty
                : activity.genericDifficulty
            )}
          </span>
        </div>
        <div className="metadata-item">
          <span className="metadata-label">Estimated Time:</span>
          <span className="metadata-value">
            {mode === 'user' && activity.activityTask
              ? activity.activityTask.estimatedHours
              : activity.estimatedHours}
            {' '}hours
          </span>
        </div>
      </div>

      {/* Required Skills */}
      {((mode !== 'user' && activity.requiredSkills) ||
        (mode === 'user' && activity.activityTask?.requiredSkills)) && (
        <div className="activity-required-skills">
          <div className="required-skills-label">Required Skills:</div>
          <div className="required-skills-list">
            {(mode === 'user'
              ? activity.activityTask.requiredSkills
              : activity.requiredSkills
            ).map((rs) => (
              <span key={rs.id} className="skill-badge">
                {rs.skillMaster?.name || rs.name} (Level {rs.minimumProficiency})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* AI Metadata (for user mode with AI analysis) */}
      {mode === 'user' && activity.aiEstimatedDifficulty && (
        <div className="activity-ai-metadata">
          <div className="metadata-item">
            <span className="metadata-label">Your Difficulty:</span>
            <span className="metadata-value">
              {getDifficultyLabel(activity.aiEstimatedDifficulty)}
            </span>
          </div>
          {activity.aiEstimatedHours && (
            <div className="metadata-item">
              <span className="metadata-label">Your Estimated Time:</span>
              <span className="metadata-value">
                {activity.aiEstimatedHours} hours
              </span>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="activity-card-actions">
        {mode === 'view' && onViewDetails && (
          <button className="btn-secondary" onClick={() => onViewDetails(activity)}>
            View Details
          </button>
        )}

        {mode === 'assign' && (
          <>
            {hasAssignments ? (
              <button
                className="btn-assigned"
                onClick={() => onViewAssignments && onViewAssignments(activity)}
              >
                ✓ Activity was assigned ({assignmentCount})
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={() => onAssignClick && onAssignClick(activity)}
              >
                Assign to User
              </button>
            )}
          </>
        )}

        {mode === 'user' && (
          <>
            {activity.status === 1 && onStatusChange && (
              <button
                className="btn-primary"
                onClick={() => onStatusChange(activity.id, 2)}
              >
                Start Activity
              </button>
            )}
            {activity.status === 2 && onStatusChange && (
              <button
                className="btn-success"
                onClick={() => onStatusChange(activity.id, 3)}
              >
                Mark Complete
              </button>
            )}
            {activity.status !== 3 && onGuidanceClick && (
              <button
                className="btn-secondary"
                onClick={() => onGuidanceClick(activity)}
              >
                Get Personalized Guidance
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityCard;
